import { useEffect, useRef, useState } from 'react'
import { deployRequest, type DeployRequestOptions } from '@/api/guardianforge/deploy'

interface DeployApiResult<T> {
  data: T | null
  error: unknown
  loading: boolean
  refresh: () => void
}

export function useDeployApi<T>(
  path: string | null,
  options?: DeployRequestOptions,
  intervalMs: number | null = null,
): DeployApiResult<T> {
  const [data, setData] = useState<T | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [loading, setLoading] = useState(true)
  const [tick, setTick] = useState(0)
  const cancelled = useRef(false)
  const queryKey = JSON.stringify(options?.query ?? null)

  useEffect(() => {
    cancelled.current = false
    let timer: number | null = null

    const run = async () => {
      if (path === null) {
        setData(null)
        setError(null)
        setLoading(false)
        return
      }

      setLoading(true)
      try {
        const value = await deployRequest<T>(path, options)
        if (!cancelled.current) {
          setData(value)
          setError(null)
        }
      } catch (err) {
        if (!cancelled.current) {
          setError(err)
        }
      } finally {
        if (!cancelled.current) {
          setLoading(false)
        }
      }
    }

    run()
    if (intervalMs && intervalMs > 0) {
      timer = window.setInterval(() => {
        if (!document.hidden) run()
      }, intervalMs)
    }

    return () => {
      cancelled.current = true
      if (timer !== null) window.clearInterval(timer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, queryKey, intervalMs, tick])

  return { data, error, loading, refresh: () => setTick((value) => value + 1) }
}
